import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import {
  ApiResponse,
  FeaturePermissionMatrix,
  LocationData,
} from '@/apis/types';
import { SensorData } from '@/apis/settings';
import {
  ApiEnvelope,
  GroupedLocationsV2Response,
} from '@/pages/Shipments/ControlTower/types';
import { AxiosResponse } from 'axios';
import { OperationType } from '@/common/enum';

//<!-------On Bording Steps-------->
//Step 1[Couriers]

interface CourierData {
  /** Unique ID of the provider (UUID string). */
  id: string;

  /** Display name of the courier (e.g., "FedEx", "UPS"). */
  title: string;

  /** Connection status of the provider: "linked", "basic", "error", or null if unknown. */
  status: 'linked' | 'basic' | 'error' | null;

  /** Whether this courier is managed internally by Zoomlogi. */
  is_zoom_logi_provider: boolean;

  /** Whether this provider integration is publicly visible. */
  is_public: boolean;

  /** Whether this provider integration is currently enabled. */
  is_enabled: boolean;

  /** Associated company courier ID, if any (UUID string). */
  company_courier: string | null;

  /** List of required credential input field names (e.g., ["API Key", "Secret Key"]). */
  required_inputs: string[];

  /** HTML-formatted instructions for how to obtain credentials or integrate. */
  instructions: string;
}

export interface GetCouriersResponse {
  data: {
    data: {
      is_success: boolean;
      status?: number;
      data?: CourierData[];
      message?: string;
    };
  };
}

export const GetCouriers = (token: string): Promise<GetCouriersResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.getAllCouriers, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Link Account

export interface LinkAccountModel {
  courier_id: string;
  credentials: Record<string, string> | null;
  client_id?: string;
  client_secret?: string;
}

export const LinkAccount = (
  token: string,
  model: LinkAccountModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.settingsCourier, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const DeleteCourierAccount = (
  id: string,
  token: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.settingsCourier}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface GetSingleCourierByIdData {
  couriers?: {
    credentials?: Record<string, string>;
  };
}

type GetSingleCourierByIdResponse = ApiResponse<GetSingleCourierByIdData>;

export const GetSingleCourierById = (
  token: string,
  id: string
): Promise<GetSingleCourierByIdResponse> => {
  const url = `${APIPaths.settingsCourier}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Step2 [Products]
export const AddProducts = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.products, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface ProductData {
  id: string;
  name: string;
  description: string | null;
  product_code: string | null;
  product_type: string;
  temperature_min: string | null;
  temperature_max: string | null;
  temperature_breach_duration: string | null;
  commercial_value: string | null;
  is_active: boolean;
  company: string;
  temperature_range: string;
  viability_window: number | null;
  temp_range_is_custom: boolean;
  temperature_unit: string;
  latitude: string | null;
  longitude: string | null;
  polygon: string | null;
  is_commercial: boolean;
  stage_of_development: string | null;
  project_id: string;
  teams: Team[];
  shock: string | null;
  light: string | null;
  tilt: string | null;
  pressure_min: string | null;
  pressure_max: string | null;
  pressure_unit: string | null;
  humidity_min: string | null;
  humidity_max: string | null;
  non_null_criteria_count: number;
  product_name: string;
  humidity_unit: string | null;
  permissions: FeaturePermissionMatrix;
}

export interface Team {
  id: string;
  name: string;
  color: string;
}

export interface GetProductsResponse {
  data: {
    data: {
      is_success: boolean;
      message?: string;
      status?: number;
      data?: ProductData[];
      page_count?: number;
      total_count?: number;
      requirement_count?: number;
    };
  };
}

export const GetProducts = (
  token: string,
  page?: number,
  search?: string,
  is_onboarding?: boolean
): Promise<GetProductsResponse> => {
  const params = new URLSearchParams();

  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }
  const query = params.toString();
  let url = query ? `${APIPaths.products}?${query}` : APIPaths.products;
  if (is_onboarding === true) {
    url = `${APIPaths.products}?by_pass=true`;
  }

  //const url = `${APIPaths.products}?${params.toString()}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetProductById = (token: string, id: string): Promise<unknown> => {
  const url = `${APIPaths.products}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const DeleteProduct = (id: string, token: string): Promise<unknown> => {
  const url = `${APIPaths.products}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Step3 [Sensors]

type GetSensorsResponse = ApiResponse<SensorData[]>;

export const GetSensors = (token: string): Promise<GetSensorsResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.getAllSensors, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface LinkSensorsAccountModel {
  sensor_id: string;
  credentials:
    | Record<string, string>
    | Array<{
        id?: string;
        name: string;
        description: string;
        is_primary: boolean;
        order_index: number;
        is_update: boolean;
        is_delete: boolean;
        credential: Record<string, string>;
      }>;
  description?: string;
  name?: string;
  id?: string;
}

export interface BulkSensorCredentialModel {
  id?: string;
  is_primary: boolean;
  description: string;
  credential_name: string;
  credentials: Record<string, string>;
  sensor_id: string;
  order_index: number;
  is_update: boolean;
  is_delete: boolean;
  is_create: boolean;
}

export interface BulkSensorCredentialPayload {
  sensor_id: string;
  sensor_description?: string;
  credentials: BulkSensorCredentialModel[];
}

export interface VerifyCompanySensorModel {
  is_primary: boolean;
  description: string;
  credentials: Record<string, string>;
  sensor_id: string;
}

export const LinkSensorsAccount = (
  token: string,
  model: LinkSensorsAccountModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.linkSensorsAccount, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const LinkSensorsAccountBulk = (
  token: string,
  model: BulkSensorCredentialPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.linkSensorsAccountBulk, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const VerifyCompanySensor = (
  token: string,
  model: VerifyCompanySensorModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.verifyCompanySensor,
        model,
        token,
        null,
        OperationType.NonBlockedWrite
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const DeleteSensor = (
  id: string,
  token: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.linkSensorsAccount}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//step 5

type AddUsersResponse = ApiResponse;

export const AddUsers = (
  token: string,
  model: unknown,
  isSignup: boolean
): Promise<AddUsersResponse> => {
  return new Promise((resolve, reject) => {
    const url = `${APIPaths.users}?is_signup=${isSignup ? 'true' : 'false'}`;
    interceptor
      .axiosPost('Post', url, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface UserTeam {
  name: string;
  role_name: string;
  color?: string;
}

export interface UserData {
  id: string;
  email: string;
  teams: UserTeam[];
  // Optional fields returned by API for user mentions
  name?: string;
  username?: string;
  user_id?: string;
  badge_color?: string;
  author_badge_color?: string;
  // Also returned by GetALLUsers (used by the Settings > Users table/row
  // state), but not by every endpoint that returns UserData — hence optional.
  is_active?: boolean;
  is_pending?: boolean;
}

export interface GetAllUsersResponse {
  data: {
    data: {
      data: UserData[];
      page_count: number;
      total_count: number;
      is_success?: boolean;
      message?: string;
    };
  };
}

export type GetUsersResponse = ApiResponse<UserData[]>;

export const GetUsers = (
  token: string,
  checked: boolean,
  page?: number,
  search?: string
): Promise<GetUsersResponse> => {
  const params = new URLSearchParams();

  params.append('is_active', (!checked).toString());

  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.users}?${params.toString()}`, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetALLUsers = (
  token: string,
  checked: boolean,
  page?: number,
  search?: string
): Promise<GetAllUsersResponse> => {
  const params = new URLSearchParams();
  if (checked === true) {
    params.append('is_active', (!checked).toString());
  }

  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.users}?${params.toString()}`, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetALLUsersForPreviewSession = (
  token: string,
  page?: number,
  search?: string
): Promise<GetAllUsersResponse> => {
  const params = new URLSearchParams();

  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.previewUsers}?${params.toString()}`,
        token,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetUserById = (token: string, id: string): Promise<unknown> => {
  const url = `${APIPaths.users}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Step 4

export const AddLocations = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.location, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface GetLocationByIdData {
  id: string;
}

type GetLocationByIdResponse = ApiResponse<GetLocationByIdData>;

export const GetLocationById = (
  token: string,
  id: string
): Promise<GetLocationByIdResponse> => {
  const url = `${APIPaths.settingsLocation}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface GetLocationsResponse {
  data: {
    data: {
      is_success: boolean;
      status?: number;
      message?: string;
      data?: {
        data: LocationData[];
      };
    };
  };
}

export const GetLocations = (
  token: string,
  checked: boolean,
  pagination = false,
  page = 1,
  search?: string
): Promise<GetLocationsResponse> => {
  let url = `${APIPaths.settingsLocation}?location_name=${checked}`;

  if (pagination === true) {
    url += `&pagination=${pagination}&page=${page}`;
  }
  if (search && search.trim().length > 0) {
    const encoded = encodeURIComponent(search.trim());
    url += `&search=${encoded}`;
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface GetSingleLocationsResponse {
  data: {
    data: {
      is_success: boolean;
      status?: number;
      message?: string;
      data: LocationData;
    };
  };
}
export const GetSingleLocation = (
  token: string,
  id: string
): Promise<GetSingleLocationsResponse> => {
  const url = `${APIPaths.settingsLocation}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface GetLocationsWithPaginationAndSearchResponse {
  data?: {
    data?: {
      is_success: boolean;
      message: string;
      page_count: number;
      total_count: number;
      data?: {
        data: LocationData[];
      };
    };
  };
}

export const GetLocationsParsedListWithPagination = (
  token: string,
  page = 1,
  limit = 20,
  search?: string
): Promise<AxiosResponse<ApiEnvelope<GroupedLocationsV2Response>>> => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('limit', String(limit));

  if (search?.trim()) {
    params.append('search', search.trim());
  }

  const url = `${APIPaths.onboardingLocationsParsedList}?${params.toString()}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null, null)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetGroupedLocationsWithPagination = (
  token: string,
  page = 1,
  limit = 20,
  search?: string,
  filterType?: string
): Promise<AxiosResponse<ApiEnvelope<GroupedLocationsV2Response>>> => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('limit', String(limit));
  if (filterType) {
    params.append('order_by_usage', String(filterType));
  }

  if (search?.trim()) params.append('search', search.trim());

  const url = `${APIPaths.onboardingGroupedLocations}?${params.toString()}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null, null)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetLocationsWithPaginationAndSearch = (
  token: string,
  checked: boolean,
  page?: number,
  search?: string
): Promise<GetLocationsWithPaginationAndSearchResponse> => {
  const params = new URLSearchParams();
  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  params.append('location_name', checked ? 'true' : 'false');
  const url = `${APIPaths.settingsLocation}?${params.toString()}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Step 6

export const SendNotification = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  const method = 'Post';
  const url = APIPaths.notifications;
  return new Promise((resolve, reject) => {
    interceptor[`axios${method}`](method, url, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetNotificationModel = (
  token: string,
  page?: number,
  search?: string,
  filterBy?: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams();
    if (page != null) {
      params.append('page', page.toString());
    }

    if (search) {
      params.append('search', search);
    }

    if (filterBy) {
      params.append('filter_by', filterBy);
    }

    let url = APIPaths.notifications;
    if (params?.size > 0) {
      url = `${APIPaths.notifications}?${params.toString()}`;
    }
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetNotificationById = (
  id: string,
  token: string
): Promise<unknown> => {
  const url = `${APIPaths.notifications}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const DeleteNotification = (
  id: string,
  token: string
): Promise<unknown> => {
  const url = `${APIPaths.notifications}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//dd call

export const GetProductTypes = (token: string): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.product_type, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};
